import { toast } from "@/hooks/use-toast";
import { apiServices } from "@/services/api.services";
import { jobId } from "@/services/global.services";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getCurrentTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
type MsgMap = Record<string, string[] | string>;

/** child key -> parent bucket (kept minimal & safe) */
const PARENT_MAP = {
  // invalid_date_format
  order_date_missing: "invalid_date_format",
  delivery_date_missing: "invalid_date_format",
  expected_order_date_format: "invalid_date_format",
  expected_delivery_date_format: "invalid_date_format",

  // sku_mapping_not_found
  sku_missing: "sku_mapping_not_found",

  // customer_mapping_not_found
  customer_mapping_missing: "customer_mapping_not_found",
  customer_id_not_found: "customer_mapping_not_found",

  // prices_invalid
  price_not_found_in_erp: "prices_invalid",
  zero_price: "prices_invalid",
  price_mismatched: "prices_invalid",
  product_not_found: "prices_invalid",

  // uom_mapping_not_found
  uom_missing_in_doc: "uom_mapping_not_found",
  uom_not_found_in_erp: "uom_mapping_not_found",

  // duplicate_orders
  push_duplicate_disabled: "duplicate_orders",
  duplicate_order: "duplicate_orders",

  // erp_failed
  erp_failed: "erp_failed",

  // erp_processed
  erp_pending: "erp_processed",
  erp_success: "erp_processed",
} as const;

type ParentKey = (typeof PARENT_MAP)[keyof typeof PARENT_MAP];

/* ----------------------------- Safe utilities ----------------------------- */

const isObject = (v: any): v is Record<string, any> =>
  v !== null && typeof v === "object" && !Array.isArray(v);

const asArray = <T = any>(v: any): T[] => (Array.isArray(v) ? v : []);

const safeStr = (v: any): string => {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return "";
  }
};

const list = (arr: any[], fmt: (x: any) => string): string => {
  const a = asArray(arr).map((x) => {
    try {
      return (fmt?.(x) ?? "").trim();
    } catch {
      return "";
    }
  });
  return a.filter(Boolean).join(", ");
};

const dedupeTrim = (msgs: string[]) =>
  Array.from(new Set(msgs.map((m) => m?.trim()).filter(Boolean)));

/* ---------------------------- Normalization step --------------------------- */
/** Accepts either nested buckets or flattened child keys at root */
function normalizePayload(input: any): Record<string, any> {
  if (!isObject(input)) return {};
  const out: Record<string, any> = { ...input };

  for (const [key, val] of Object.entries(input)) {
    const parent = (PARENT_MAP as Record<string, ParentKey | undefined>)[key];
    if (!parent) continue;

    // ensure parent container
    if (!isObject(out[parent])) out[parent] = {};
    const bucket = out[parent];

    if (Array.isArray(val)) {
      const prev = asArray(bucket[key]);
      bucket[key] = prev.concat(val);
    } else if (val !== undefined) {
      bucket[key] = val;
    }

    // avoid double-processing
    delete out[key];
  }

  return out;
}

/* ----------------------------- Main function ------------------------------ */
/**
 * Summarize errors safely.
 * - combine=false => values are string[] (default)
 * - combine=true  => values are "|||"-joined string
 * - includeEmptyKeys=false => drops keys that produced no message
 */
export function summarizeErrors(
  payload: Record<string, any>,
  combine = false,
  includeEmptyKeys = false,
): MsgMap {
  const normalized = normalizePayload(payload);
  const result: MsgMap = {};

  const setOut = (key: string, msgs: string[]) => {
    const cleaned = dedupeTrim(msgs);
    if (!cleaned.length && !includeEmptyKeys) return; // drop empty buckets
    result[key] = combine ? cleaned.join(" ||| ") : cleaned;
  };

  for (const [key, val] of Object.entries(normalized)) {
    const messages: string[] = [];

    try {
      switch (key as ParentKey | string) {
        case "invalid_date_format": {
          if (isObject(val)) {
            if (val.order_date_missing) messages.push("Order date not found.");
            if (val.delivery_date_missing)
              messages.push("Delivery date not found.");
            if (val.expected_delivery_date_format)
              messages.push(
                "Delivery date format validation failed, expected format from retailer settings.",
              );
            if (val.expected_order_date_format)
              messages.push(
                "Order date format validation failed, expected format from retailer settings.",
              );
          }
          break;
        }

        case "sku_mapping_not_found": {
          const skus = list(val?.sku_missing, (x) => x?.po_sku ?? safeStr(x));
          if (skus)
            messages.push(`SKU mapping not found for PO SKUs: ${skus}.`);
          break;
        }

        case "erp_disabled": {
          // accept any truthy marker/string
          if (val?.erp_disabled || typeof val === "string" || val === true)
            messages.push("ERP disabled, please configure in settings.");
          break;
        }

        case "customer_mapping_not_found": {
          if (val?.customer_mapping_missing)
            messages.push("Customer mapping not found.");
          if (val?.customer_id_not_found)
            messages.push("Customer ID or Store ID not found.");
          break;
        }

        case "prices_invalid": {
          const notFound = list(val?.price_not_found_in_erp, (x) => {
            const sku = safeStr(x?.erp_sku);
            const uom = safeStr(x?.uom);
            return sku ? `${sku}${uom ? ` (${uom})` : ""}` : "";
          });
          if (notFound)
            messages.push(`Price not found in ERP for SKUs: ${notFound}.`);

          const zeros = list(val?.zero_price, (x) => {
            const sku = safeStr(x?.erp_sku);
            const uom = safeStr(x?.uom);
            return sku ? `${sku}${uom ? ` (${uom})` : ""}` : "";
          });
          if (zeros) messages.push(`0$ found for SKUs: ${zeros}.`);

          const mism = list(val?.price_mismatched, (x) => {
            const sku = safeStr(x?.erp_sku);
            const exp = safeStr(x?.expected_price);
            const act = safeStr(x?.actual_price);
            return sku ? `${sku} (expected ${exp}, actual ${act})` : "";
          });
          if (mism) messages.push(`Price mismatch for SKUs: ${mism}.`);

          const prodNF = list(val?.product_not_found, (x) => {
            const sku = safeStr(x?.erp_sku);
            const uom = safeStr(x?.uom);
            return sku ? `${sku}${uom ? ` (${uom})` : ""}` : "";
          });
          if (prodNF)
            messages.push(`Product not found in ERP for SKUs: ${prodNF}.`);
          break;
        }

        case "uom_mapping_not_found": {
          const missingInDoc = list(val?.uom_missing_in_doc, (x) => {
            const po = safeStr(x?.po_sku);
            const erp = safeStr(x?.erp_sku);
            return po || erp ? `${po}→${erp}` : "";
          });
          if (missingInDoc)
            messages.push(
              `UOM missing in document for PO SKUs: ${missingInDoc}.`,
            );

          const notFoundERP = list(val?.uom_not_found_in_erp, (x) => {
            const sku = safeStr(x?.erp_sku);
            const uom = safeStr(x?.uom);
            return sku ? `${sku}${uom ? ` (${uom})` : ""}` : "";
          });
          if (notFoundERP)
            messages.push(`UOM not found in ERP for SKUs: ${notFoundERP}.`);
          break;
        }

        case "duplicate_orders": {
          if (val?.push_duplicate_disabled)
            messages.push(
              "Order not pushed: push_duplicate/amend_and_push_duplicate settings disabled.",
            );
          if (val?.duplicate_order)
            messages.push("Duplicate order found in SellnChill.");
          break;
        }

        case "erp_failed": {
          if (val?.erp_failed || typeof val === "string" || val === true)
            messages.push("ERP failed due to failure from ERP system.");
          break;
        }

        case "erp_processed": {
          if (val?.erp_pending)
            messages.push("Pushed to SellnChill & waiting for SNC response.");
          if (val?.erp_success) {
            const ref = String(val.erp_success).match(/([A-Z0-9-]+)/)?.[1];
            messages.push(
              `Order pushed successfully${ref ? ` (Ref: ${ref})` : ""}.`,
            );
          }
          break;
        }

        default: {
          // Unknown parent bucket: try best-effort pretty print
          if (typeof val === "string") {
            messages.push(val);
          } else if (Array.isArray(val) && val.length) {
            // arrays of { erp_sku, uom } or primitives
            const pretty = list(val, (x) => {
              if (isObject(x)) {
                const sku = safeStr((x as any).erp_sku ?? (x as any).po_sku);
                const uom = safeStr((x as any).uom);
                return sku ? `${sku}${uom ? ` (${uom})` : ""}` : safeStr(x);
              }
              return safeStr(x);
            });
            if (pretty) messages.push(pretty);
          } else if (isObject(val)) {
            // flatten simple string props
            for (const [k, v] of Object.entries(val)) {
              const s = safeStr(v);
              if (s) messages.push(`${k}: ${s}`);
            }
          } else {
            const s = safeStr(val);
            if (s) messages.push(s);
          }
        }
      }
    } catch {
      // swallow any unexpected runtime errors and continue
    }

    setOut(key, messages);
  }

  return result;
}

function combineValues(list: any[], separator = " - ") {
  return list
    .filter((obj) => {
      // Keep only objects that have at least one non-empty value
      return Object.values(obj).some((val) => val !== "");
    })
    .map((obj) => Object.values(obj).join(separator));
}

const getArrayMessage = (prop: any) => {
  const isSingle = Object.values(prop[0]).length === 1;
  if (Array.isArray(prop) && typeof prop[0] === "string") {
    return {
      keyName: null,
      data: prop,
    };
  } else if (isSingle) {
    const list = prop.map((obj: any) => Object.values(obj)[0]);
    return {
      keyName: Object.keys(prop[0])[0],
      data: list.filter((item: string) => item !== ""),
    };
  } else {
    return {
      keyName: "(" + Object.keys(prop[0]).join(" - ") + ")",
      data: combineValues(prop),
    };
  }
};

function isValidJsonString(value: any) {
  if (typeof value !== "string") return false;
  try {
    const parsed = JSON.parse(value);
    return parsed !== null && typeof parsed === "object";
  } catch {
    return false;
  }
}

const getSummaryMessage = (prop: any) => {
  let list: any = [];
  for (const key in prop) {
    const value = prop[key];
    if (typeof value === "string") {
      list = [
        ...list,
        {
          type: "string",
          message: value,
        },
      ];
    } else if (isValidJsonString(value)) {
      console.log({ value });
    } else if (Array.isArray(value)) {
      const tempList = getArrayMessage(value);
      const message = tempList?.keyName
        ? `${(key as any).replaceAll("_", " ")} for ` +
        tempList?.keyName +
        " : "
        : `${(key as any).replaceAll("_", " ")}:`;
      list = [
        ...list,
        {
          type: "array",
          message: message,
          data: tempList.data,
        },
      ];
    }
  }
  return list;
};

export const getSummary = (prop: any) => {
  let tempList = {};
  for (const key in prop) {
    tempList = { ...tempList, [key]: getSummaryMessage(prop[key]) };
  }
  return tempList;
};




const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const getJobId = async (prop: string, isInProgressExist?: boolean) => {
  // const startTime = Date.now();
  // const timeout = 30000; // 30 seconds

  while (true) {
    // Timeout check
    // if (Date.now() - startTime > timeout) {
    //   toast({
    //     title: "Timeout",
    //     variant: "destructive",
    //     description: "Request timeout. Please try again.",
    //   });

    //   return {
    //     data: null,
    //     error: "Request timeout. Please try again.",
    //   };
    // }

    const { data, error, message } = await apiServices<any>(
      jobId.get + prop,
      "get"
    );

    if (data?.process_status !== "in-progress" || isInProgressExist) {
      if (data?.error) {
        toast({
          title: "Error",
          variant: "destructive",
          description: error?.status?.message || message,
        });
      }

      if (data?.status?.error_details?.fail_message) {
        toast({
          title: "Error",
          variant: "destructive",
          description: data.status.error_details.fail_message,
        });

        return {
          data: null,
          error: data.status.error_details.fail_message,
        };
      }

      return { data, error };
    }

    // Wait 5 sec before next retry
    await sleep(5000);
  }
};